/**
 * WorkspaceSettingAutomations — sl-tdgy.8, sl-auz2.2.2
 *
 * Centralized workflow configuration tab in Workspace Settings.
 * Sections: Plan Limits, Safety (Circuit Breaker with config).
 * Cost Control + Usage Overview sections are scope of sl-7q0s (Workflow Billing).
 *
 * sl-auz2.2.2: Visible to all roles. Non-admins see read-only view
 * (Plan Limits, Queue Activity, Usage Overview). Circuit Breaker config
 * and Cost Control are admin-only.
 */
import { notify, Slider, Switch } from '@affine/component';
import {
  SettingHeader,
  SettingRow,
  SettingWrapper,
} from '@affine/component/setting-components';
import { useBackendAvailable } from '@affine/core/components/hooks/use-backend-availability';
import { useWorkspaceInfo } from '@affine/core/components/hooks/use-workspace-info';
import {
  type UserSettings,
  UserSettingsService,
} from '@affine/core/modules/cloud';
import { useFeatureGate } from '@affine/core/modules/streamlain/hooks/use-feature-gate';
import { formatDuration } from '@affine/core/modules/streamlain/utils/workflow-limits';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { Permission } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import {
  type CSSProperties,
  type FC,
  useCallback,
  useEffect,
  useState,
} from 'react';

import {
  SettingsOfflineHeaderAction,
  useSettingsOfflineIncompleteLabel,
} from '../../shared/settings-offline-status';
import { BudgetLimitsToggleRow } from '../ai/budget-limits-toggle-row';
import { CreditBalanceWidget } from '../ai/credit-balance-widget';
import { UsageAnalytics } from '../ai/usage-analytics';
import { SettingsEditorScopeHint } from '../shared/settings-editor-scope-hint';
import { requestWorkspaceSettingGraphQL } from '../shared/workspace-settings-api';

// =============================================================================
// Styles
// =============================================================================

// Native <select> falls back to OS dark-mode rendering (dark text on dark blue)
// unless background/color are set explicitly. Token-based styling keeps the
// control legible across affine/blue-white/dark themes.
const selectStyle: CSSProperties = {
  padding: '4px 10px',
  borderRadius: 6,
  border: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
  backgroundColor: 'var(--streamlain-v2-layer-background-primary)',
  color: 'var(--streamlain-v2-text-primary)',
  fontSize: 12,
  cursor: 'pointer',
};

const usageSectionDescriptionStyle: CSSProperties = {
  marginBottom: 10,
  fontSize: 12,
  lineHeight: '20px',
  color: 'var(--affine-text-secondary-color)',
};

const sectionDividerStyle: CSSProperties = {
  borderTop: '1px solid var(--streamlain-border-color)',
  margin: '24px 0 8px',
};

// =============================================================================
// Types
// =============================================================================

interface CircuitBreakerConfig {
  enabled: boolean;
  threshold: number;
  cooldownMode: string;
}

interface WorkflowLimitsWithCB {
  maxStepsPerWorkflow: number;
  maxStepDurationMs: number;
  maxWorkflowDurationMs: number;
  maxConcurrentWorkflows: number;
  // Plan caps — the slider upper bound. Effective values above are
  // already clamped server-side so they never exceed these.
  planMaxStepDurationMs: number;
  planMaxWorkflowDurationMs: number;
  concurrentCurrent: number;
  circuitBreakerEnabled: boolean;
  circuitBreakerThreshold: number;
  circuitBreakerCooldownMode: string;
  // sl-38sb.2.2: Queue metrics
  queueRejectCount24h: number;
  queueAvgWaitMs: number;
  queueWaitSamples: number;
}

// Timeout controls stay finer-grained than billing. Workflow billing rounds
// separately to 30s ticks, but admins can still cap runaway steps at 15s.
const TIMEOUT_STEP_MS = 15_000;
const TIMEOUT_MIN_MS = 15_000;

function resetButtonStyle(disabled: boolean): CSSProperties {
  return {
    padding: '4px 14px',
    borderRadius: 6,
    border: '1px solid var(--streamlain-v2-layer-insideBorder-border)',
    background: 'var(--streamlain-v2-layer-background-primary)',
    color: 'var(--streamlain-v2-text-primary)',
    fontSize: 12,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
  };
}

interface TimeoutSliderCellProps {
  value: number;
  min: number;
  max: number;
  step: number;
  disabled?: boolean;
  onChange: (next: number) => void;
}

const TimeoutSliderCell = ({
  value,
  min,
  max,
  step,
  disabled,
  onChange,
}: TimeoutSliderCellProps) => {
  // Clamp the displayed value so a moving `min` (workflow-timeout follows
  // step-timeout) doesn't leave the thumb behind the lower bound.
  const safeValue = Math.min(Math.max(value, min), max);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        minWidth: 220,
      }}
    >
      <Slider
        value={[safeValue]}
        min={min}
        max={max}
        step={step}
        disabled={disabled || max <= min}
        width={180}
        onValueChange={values => {
          const next = values[0];
          if (typeof next === 'number') onChange(next);
        }}
      />
      <span
        style={{
          minWidth: 56,
          textAlign: 'right',
          fontSize: 12,
          color: 'var(--streamlain-v2-text-primary)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {formatDuration(safeValue)}
      </span>
    </div>
  );
};

// =============================================================================
// GraphQL Helpers
// =============================================================================

const LIMITS_FRAGMENT = `
  maxStepsPerWorkflow maxStepDurationMs maxWorkflowDurationMs
  maxConcurrentWorkflows concurrentCurrent
  planMaxStepDurationMs planMaxWorkflowDurationMs
  circuitBreakerEnabled circuitBreakerThreshold circuitBreakerCooldownMode
  queueRejectCount24h queueAvgWaitMs queueWaitSamples
`;

const LIMITS_QUERY = `query WorkflowLimits($id: String!) {
  workspace(id: $id) {
    workflowLimits {${LIMITS_FRAGMENT}}
  }
}`;

const UPDATE_CB_MUTATION = `mutation UpdateCB($input: UpdateCircuitBreakerConfigInput!) {
  updateCircuitBreakerConfig(input: $input)
}`;

const UPDATE_LIMITS_MUTATION = `mutation UpdateLimits($input: UpdateWorkflowLimitsInput!) {
  updateWorkflowLimits(input: $input) {${LIMITS_FRAGMENT}}
}`;

const RESET_LIMITS_MUTATION = `mutation ResetLimits($workspaceId: String!) {
  resetWorkflowLimits(workspaceId: $workspaceId) {${LIMITS_FRAGMENT}}
}`;

// sl-9fmv.10.5: Experimental lifecycle override — one round-trip per flip.
const EXPERIMENTAL_FLAG_QUERY = `query ExperimentalNodesFlag($id: String!) {
  workspace(id: $id) {
    experimentalNodesEnabled
  }
}`;

const UPDATE_EXPERIMENTAL_FLAG_MUTATION = `mutation UpdateExperimentalFlag($input: UpdateWorkspaceInput!) {
  updateWorkspace(input: $input) {
    id
    experimentalNodesEnabled
  }
}`;

async function gqlFetch<T>(
  workspaceId: string,
  query: string,
  variables: Record<string, unknown>
): Promise<T | null> {
  try {
    const res = await requestWorkspaceSettingGraphQL(
      workspaceId,
      query,
      variables
    );
    if (!res.ok) return null;
    const json = await res.json();
    return json?.data ?? null;
  } catch {
    return null;
  }
}

// =============================================================================
// Component
// =============================================================================

export const WorkspaceSettingAutomations: FC = () => {
  const t = useI18n();
  const backendAvailable = useBackendAvailable();
  const offlineLabel = useSettingsOfflineIncompleteLabel();
  const workspaceService = useService(WorkspaceService);
  const workspace = workspaceService.workspace;
  const workspaceId = workspace?.id;
  const information = useWorkspaceInfo(workspace);
  const isAdmin = !!(information?.isOwner || information?.isAdmin);
  const isEditor = information?.role === Permission.Editor;
  const { plan } = useFeatureGate();
  const [limits, setLimits] = useState<WorkflowLimitsWithCB | null>(null);
  const [saving, setSaving] = useState(false);
  const [limitsSaving, setLimitsSaving] = useState(false);
  // Local slider values — debounced-saved so dragging feels smooth
  // without hammering the mutation on every frame. Initialised from the
  // server response once the first query completes.
  const [stepDraftMs, setStepDraftMs] = useState<number | null>(null);
  const [workflowDraftMs, setWorkflowDraftMs] = useState<number | null>(null);
  // sl-9fmv.10.5: per-workspace override for experimental lifecycle node types
  const [experimentalEnabled, setExperimentalEnabled] = useState<
    boolean | null
  >(null);
  const [experimentalSaving, setExperimentalSaving] = useState(false);

  // AI relation matching — user-level setting, shown here alongside automation config
  const userSettingsService = useService(UserSettingsService);
  const userSettings = useLiveData(userSettingsService.userSettings$);
  const [aiRelationMutating, setAiRelationMutating] = useState(false);
  const [usageOverviewPeriodTitle, setUsageOverviewPeriodTitle] =
    useState<string>('');
  const handleAiRelationUpdate = useCallback(
    (value: boolean) => {
      setAiRelationMutating(true);
      userSettingsService
        .updateUserSettings({
          aiRelationMatchingEnabled: value,
        } as Partial<UserSettings>)
        .catch(() => notify.error({ title: 'Failed to save' }))
        .finally(() => setAiRelationMutating(false));
    },
    [userSettingsService]
  );

  // Load limits + CB config
  useEffect(() => {
    if (!backendAvailable || !workspaceId || !isAdmin) return;
    let cancelled = false;
    void gqlFetch<{ workspace: { workflowLimits: WorkflowLimitsWithCB } }>(
      workspaceId,
      LIMITS_QUERY,
      { id: workspaceId }
    ).then(data => {
      if (!cancelled && data?.workspace?.workflowLimits) {
        const fresh = data.workspace.workflowLimits;
        setLimits(fresh);
        setStepDraftMs(fresh.maxStepDurationMs);
        setWorkflowDraftMs(fresh.maxWorkflowDurationMs);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [backendAvailable, isAdmin, workspaceId]);

  // Debounce-save timeout overrides. 400ms gives enough room to finish
  // dragging without a midflight mutation, while still feeling snappy on
  // individual clicks. Only fires when draft values diverge from the
  // last server-confirmed values.
  useEffect(() => {
    if (!backendAvailable || !workspaceId || !isAdmin) return;
    if (!limits || stepDraftMs == null || workflowDraftMs == null) return;
    if (
      stepDraftMs === limits.maxStepDurationMs &&
      workflowDraftMs === limits.maxWorkflowDurationMs
    ) {
      return;
    }
    const handle = setTimeout(() => {
      setLimitsSaving(true);
      void gqlFetch<{ updateWorkflowLimits: WorkflowLimitsWithCB }>(
        workspaceId,
        UPDATE_LIMITS_MUTATION,
        {
          input: {
            workspaceId,
            stepTimeoutMs: stepDraftMs,
            workflowTimeoutMs: workflowDraftMs,
          },
        }
      )
        .then(data => {
          if (data?.updateWorkflowLimits) {
            setLimits(data.updateWorkflowLimits);
            setStepDraftMs(data.updateWorkflowLimits.maxStepDurationMs);
            setWorkflowDraftMs(data.updateWorkflowLimits.maxWorkflowDurationMs);
          }
        })
        .catch(() => {
          notify.error({ title: 'Failed to save timeout limits' });
        })
        .finally(() => setLimitsSaving(false));
    }, 400);
    return () => clearTimeout(handle);
  }, [
    backendAvailable,
    workspaceId,
    isAdmin,
    limits,
    stepDraftMs,
    workflowDraftMs,
  ]);

  const resetLimits = useCallback(async () => {
    if (!workspaceId || limitsSaving) return;
    setLimitsSaving(true);
    try {
      const data = await gqlFetch<{
        resetWorkflowLimits: WorkflowLimitsWithCB;
      }>(workspaceId, RESET_LIMITS_MUTATION, { workspaceId });
      if (data?.resetWorkflowLimits) {
        setLimits(data.resetWorkflowLimits);
        setStepDraftMs(data.resetWorkflowLimits.maxStepDurationMs);
        setWorkflowDraftMs(data.resetWorkflowLimits.maxWorkflowDurationMs);
        notify.success({ title: 'Reset to plan default' });
      }
    } catch {
      notify.error({ title: 'Reset failed' });
    } finally {
      setLimitsSaving(false);
    }
  }, [workspaceId, limitsSaving]);

  // sl-9fmv.10.5: Load experimentalNodesEnabled flag
  useEffect(() => {
    if (!backendAvailable || !workspaceId || !isAdmin) return;
    let cancelled = false;
    void gqlFetch<{ workspace: { experimentalNodesEnabled: boolean } }>(
      workspaceId,
      EXPERIMENTAL_FLAG_QUERY,
      { id: workspaceId }
    ).then(data => {
      if (
        !cancelled &&
        typeof data?.workspace?.experimentalNodesEnabled === 'boolean'
      ) {
        setExperimentalEnabled(data.workspace.experimentalNodesEnabled);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [backendAvailable, isAdmin, workspaceId]);

  // sl-9fmv.10.5: Toggle experimentalNodesEnabled — optimistic update with
  // rollback on error so the switch feels responsive; failure shows a notify.
  const toggleExperimental = useCallback(
    async (next: boolean) => {
      if (!workspaceId || experimentalSaving) return;
      const previous = experimentalEnabled;
      setExperimentalEnabled(next);
      setExperimentalSaving(true);
      try {
        const data = await gqlFetch<{
          updateWorkspace: { experimentalNodesEnabled: boolean };
        }>(workspaceId, UPDATE_EXPERIMENTAL_FLAG_MUTATION, {
          input: { id: workspaceId, experimentalNodesEnabled: next },
        });
        if (!data) throw new Error('updateWorkspace returned null');
        setExperimentalEnabled(data.updateWorkspace.experimentalNodesEnabled);
        notify.success({ title: 'Saved' });
      } catch {
        setExperimentalEnabled(previous);
        notify.error({ title: 'Failed to save' });
      } finally {
        setExperimentalSaving(false);
      }
    },
    [workspaceId, experimentalEnabled, experimentalSaving]
  );

  // Update circuit breaker config
  const updateCB = useCallback(
    async (patch: Partial<CircuitBreakerConfig>) => {
      if (!workspaceId || saving) return;
      setSaving(true);
      try {
        await gqlFetch(workspaceId, UPDATE_CB_MUTATION, {
          input: { workspaceId, ...patch },
        });
        // Refresh
        const data = await gqlFetch<{
          workspace: { workflowLimits: WorkflowLimitsWithCB };
        }>(workspaceId, LIMITS_QUERY, { id: workspaceId });
        if (data?.workspace?.workflowLimits) {
          setLimits(data.workspace.workflowLimits);
        }
        notify.success({ title: 'Saved' });
      } catch {
        notify.error({ title: 'Failed to save' });
      } finally {
        setSaving(false);
      }
    },
    [workspaceId, saving]
  );

  return (
    <>
      <SettingHeader
        title={
          t['com.streamlain.settings.workspace.automations.title']?.() ??
          'Workflow Limits'
        }
        subtitle={
          t['com.streamlain.settings.workspace.automations.subtitle']?.() ??
          'Configure workflow execution limits, safety, and defaults for this workspace.'
        }
        action={<SettingsOfflineHeaderAction show={!backendAvailable} />}
      />
      <>
        {/* sl-m82f.3.2: Credit balance widget reused from workspace:ai with
              the workflow sub-bar highlighted, so admins see the same top-down
              ledger view here as well. */}
        {isAdmin ? <CreditBalanceWidget highlightContext="workflow" /> : null}

        {/* Section 1: Plan Limits */}
        {isAdmin ? (
          <SettingWrapper
            title={
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                {t[
                  'com.streamlain.settings.workspace.automations.limits'
                ]?.() ?? 'Plan Limits'}
                <span
                  data-paid={plan !== 'Free'}
                  style={{
                    padding: '1px 8px',
                    borderRadius: 10,
                    fontSize: '10px',
                    fontWeight: 600,
                    lineHeight: '18px',
                    whiteSpace: 'nowrap',
                    color:
                      plan !== 'Free'
                        ? '#fff'
                        : 'var(--streamlain-v2-text-emphasis, var(--streamlain-v2-text-secondary))',
                    background:
                      plan !== 'Free'
                        ? 'var(--streamlain-v2-button-primary)'
                        : 'var(--streamlain-v2-layer-background-hoverOverlay)',
                    border:
                      plan !== 'Free'
                        ? 'none'
                        : '1px solid var(--streamlain-v2-layer-insideBorder-border)',
                  }}
                >
                  {plan}
                </span>
              </span>
            }
          >
            {limits ? (
              <>
                <SettingRow
                  name={
                    t[
                      'com.streamlain.settings.workspace.automations.step-timeout'
                    ]?.() ?? 'Step Timeout'
                  }
                  desc={
                    t[
                      'com.streamlain.settings.workspace.automations.step-timeout-desc'
                    ]?.() ?? 'Maximum duration per workflow step.'
                  }
                >
                  {isAdmin ? (
                    <TimeoutSliderCell
                      value={stepDraftMs ?? limits.maxStepDurationMs}
                      min={TIMEOUT_MIN_MS}
                      max={limits.planMaxStepDurationMs}
                      step={TIMEOUT_STEP_MS}
                      disabled={limitsSaving}
                      onChange={next => {
                        setStepDraftMs(next);
                        // Invariant: workflow >= step. Drag step past the
                        // current workflow cap and we lift workflow with
                        // it rather than silently letting the backend clamp.
                        setWorkflowDraftMs(prev =>
                          prev == null ? next : Math.max(prev, next)
                        );
                      }}
                    />
                  ) : (
                    <span>{formatDuration(limits.maxStepDurationMs)}</span>
                  )}
                </SettingRow>
                <SettingRow
                  name={
                    t[
                      'com.streamlain.settings.workspace.automations.workflow-timeout'
                    ]?.() ?? 'Workflow Timeout'
                  }
                  desc={
                    t[
                      'com.streamlain.settings.workspace.automations.workflow-timeout-desc'
                    ]?.() ?? 'Maximum total duration per workflow execution.'
                  }
                >
                  {isAdmin ? (
                    <TimeoutSliderCell
                      value={workflowDraftMs ?? limits.maxWorkflowDurationMs}
                      // Workflow timeout can never drop below the current
                      // step timeout — a workflow shorter than one of its
                      // steps would fail before the step finished.
                      min={stepDraftMs ?? limits.maxStepDurationMs}
                      max={limits.planMaxWorkflowDurationMs}
                      step={TIMEOUT_STEP_MS}
                      disabled={limitsSaving}
                      onChange={setWorkflowDraftMs}
                    />
                  ) : (
                    <span>{formatDuration(limits.maxWorkflowDurationMs)}</span>
                  )}
                </SettingRow>
                {isAdmin && (
                  <SettingRow
                    name={
                      t[
                        'com.streamlain.settings.workspace.automations.reset-limits'
                      ]?.() ?? 'Reset to plan default'
                    }
                    desc={
                      t[
                        'com.streamlain.settings.workspace.automations.reset-limits-desc'
                      ]?.() ??
                      'Clear workspace-level overrides and use the plan caps.'
                    }
                  >
                    <button
                      type="button"
                      style={resetButtonStyle(limitsSaving)}
                      disabled={limitsSaving}
                      onClick={() => void resetLimits()}
                    >
                      {t[
                        'com.streamlain.settings.workspace.automations.reset-limits-action'
                      ]?.() ?? 'Reset'}
                    </button>
                  </SettingRow>
                )}
                <SettingRow
                  name={
                    t[
                      'com.streamlain.settings.workspace.automations.max-steps'
                    ]?.() ?? 'Max Steps'
                  }
                  desc={
                    t[
                      'com.streamlain.settings.workspace.automations.max-steps-desc'
                    ]?.() ?? 'Maximum number of steps per workflow definition.'
                  }
                >
                  <span>{limits.maxStepsPerWorkflow}</span>
                </SettingRow>
                <SettingRow
                  name={
                    t[
                      'com.streamlain.settings.workspace.automations.concurrent'
                    ]?.() ?? 'Concurrent Workflows'
                  }
                  desc={
                    t[
                      'com.streamlain.settings.workspace.automations.concurrent-desc'
                    ]?.() ??
                    'Maximum number of workflows running at the same time.'
                  }
                >
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {limits.maxConcurrentWorkflows}
                  </span>
                </SettingRow>
              </>
            ) : (
              <SettingRow
                name={backendAvailable ? 'Loading...' : offlineLabel}
                desc=""
              >
                <span />
              </SettingRow>
            )}
          </SettingWrapper>
        ) : null}

        {/* Section 2: Safety (Circuit Breaker) — Admin-only (sl-auz2.2.2) */}
        {isAdmin && (
          <SettingWrapper
            title={
              t['com.streamlain.settings.workspace.automations.safety']?.() ??
              'Safety'
            }
          >
            <SettingRow
              name={
                t[
                  'com.streamlain.settings.workspace.automations.circuit-breaker'
                ]?.() ?? 'Circuit Breaker'
              }
              desc={
                t[
                  'com.streamlain.settings.workspace.automations.circuit-breaker-desc'
                ]?.() ??
                'Automatically disables scheduled workflows after consecutive failures.'
              }
            >
              <Switch
                checked={limits?.circuitBreakerEnabled ?? true}
                onChange={checked => void updateCB({ enabled: checked })}
                disabled={saving || !limits}
              />
            </SettingRow>

            {limits?.circuitBreakerEnabled && (
              <>
                <SettingRow
                  name={
                    t[
                      'com.streamlain.settings.workspace.automations.cb-threshold'
                    ]?.() ?? 'Failure Threshold'
                  }
                  desc={
                    t[
                      'com.streamlain.settings.workspace.automations.cb-threshold-desc'
                    ]?.() ??
                    'Number of consecutive failures before auto-disabling (3-10).'
                  }
                >
                  <select
                    value={limits.circuitBreakerThreshold}
                    onChange={e =>
                      void updateCB({ threshold: Number(e.target.value) })
                    }
                    disabled={saving}
                    style={selectStyle}
                  >
                    {[3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                      <option key={n} value={n}>
                        {n} failures
                      </option>
                    ))}
                  </select>
                </SettingRow>

                <SettingRow
                  name={
                    t[
                      'com.streamlain.settings.workspace.automations.cb-cooldown'
                    ]?.() ?? 'Cooldown'
                  }
                  desc={
                    t[
                      'com.streamlain.settings.workspace.automations.cb-cooldown-desc'
                    ]?.() ??
                    'After auto-disable: wait and re-enable, or require manual re-enable.'
                  }
                >
                  <select
                    value={limits.circuitBreakerCooldownMode}
                    onChange={e =>
                      void updateCB({ cooldownMode: e.target.value })
                    }
                    disabled={saving}
                    style={selectStyle}
                  >
                    <option value="manual">Manual</option>
                    <option value="1h">1 hour</option>
                    <option value="6h">6 hours</option>
                    <option value="24h">24 hours</option>
                  </select>
                </SettingRow>
              </>
            )}
          </SettingWrapper>
        )}

        {/* Section 2b: KI & Automatisierung — AI relation matching per user */}
        <SettingWrapper title={t['com.streamlain.setting.automation.title']()}>
          <SettingRow
            name={t[
              'com.streamlain.setting.automation.ai-relation-matching.title'
            ]()}
            desc={t[
              'com.streamlain.setting.automation.ai-relation-matching.subtitle'
            ]()}
          >
            <Switch
              data-testid="automation-ai-relation-matching-trigger"
              checked={userSettings?.aiRelationMatchingEnabled ?? false}
              disabled={
                aiRelationMutating || !userSettings || !backendAvailable
              }
              onChange={handleAiRelationUpdate}
            />
          </SettingRow>
        </SettingWrapper>

        {/* Section 2c: Experimental Features (sl-9fmv.10.5) — Admin-only, hidden */}
        {isAdmin && false && (
          <SettingWrapper
            title={
              t[
                'com.streamlain.settings.workspace.automations.experimental-features'
              ]?.() ?? 'Experimental Features'
            }
          >
            <SettingRow
              name={
                t[
                  'com.streamlain.settings.workspace.automations.experimental-nodes'
                ]?.() ?? 'Experimental Node Types'
              }
              desc={
                t[
                  'com.streamlain.settings.workspace.automations.experimental-nodes-desc'
                ]?.() ??
                'Allow inserting node types that are still in experimental status.'
              }
            >
              <Switch
                data-testid="automations-experimental-nodes-toggle"
                checked={experimentalEnabled ?? false}
                onChange={checked => void toggleExperimental(checked)}
                disabled={experimentalSaving || experimentalEnabled === null}
              />
            </SettingRow>
          </SettingWrapper>
        )}

        {/* Section 3: Queue Activity (sl-38sb.2.2) */}
        {isAdmin ? (
          <SettingWrapper
            title={
              t[
                'com.streamlain.settings.workspace.automations.queue-activity'
              ]?.() ?? 'Queue Activity (24h)'
            }
          >
            {limits ? (
              limits.queueRejectCount24h === 0 &&
              limits.queueWaitSamples === 0 ? (
                <SettingRow
                  name={
                    t[
                      'com.streamlain.settings.workspace.automations.no-queue-activity'
                    ]?.() ?? 'No queue activity in the last 24 hours'
                  }
                  desc=""
                >
                  <span />
                </SettingRow>
              ) : (
                <>
                  <SettingRow
                    name={
                      t[
                        'com.streamlain.settings.workspace.automations.queue-rejected'
                      ]?.() ?? 'Rejected'
                    }
                    desc={
                      t[
                        'com.streamlain.settings.workspace.automations.queue-rejected-desc'
                      ]?.() ?? 'Executions rejected after soft-queue timeout.'
                    }
                  >
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {limits.queueRejectCount24h}
                    </span>
                  </SettingRow>
                  <SettingRow
                    name={
                      t[
                        'com.streamlain.settings.workspace.automations.queue-avg-wait'
                      ]?.() ?? 'Avg Wait Time'
                    }
                    desc={
                      t[
                        'com.streamlain.settings.workspace.automations.queue-avg-wait-desc'
                      ]?.() ??
                      'Average time executions waited in the soft-queue before proceeding.'
                    }
                  >
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {limits.queueAvgWaitMs < 1000
                        ? `${Math.round(limits.queueAvgWaitMs)}ms`
                        : `${(limits.queueAvgWaitMs / 1000).toFixed(1)}s`}
                    </span>
                  </SettingRow>
                  <SettingRow
                    name={
                      t[
                        'com.streamlain.settings.workspace.automations.queue-total'
                      ]?.() ?? 'Total Queued'
                    }
                    desc={
                      t[
                        'com.streamlain.settings.workspace.automations.queue-total-desc'
                      ]?.() ??
                      'Total executions that waited in the soft-queue (including successful ones).'
                    }
                  >
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {limits.queueWaitSamples}
                    </span>
                  </SettingRow>
                </>
              )
            ) : (
              <SettingRow
                name={backendAvailable ? 'Loading...' : offlineLabel}
                desc=""
              >
                <span />
              </SettingRow>
            )}
          </SettingWrapper>
        ) : null}

        {/* Section 4: Budget Limits — reuses AI BudgetLimitsToggleRow (DRY) */}
        {isAdmin && <BudgetLimitsToggleRow isAdmin={isAdmin} />}

        {/* Section 5: Workflow Usage — reuses AI UsageAnalytics, filtered to workflows only */}
        {isAdmin ? (
          <SettingWrapper
            title={
              usageOverviewPeriodTitle
                ? `${t['com.streamlain.settings.workspace.automations.usage-overview']?.() ?? 'Usage Overview'} · ${usageOverviewPeriodTitle.split(' (')[0]}`
                : (t[
                    'com.streamlain.settings.workspace.automations.usage-overview'
                  ]?.() ?? 'Usage Overview')
            }
          >
            <div style={sectionDividerStyle} />
            <div style={usageSectionDescriptionStyle}>
              {t[
                'com.streamlain.settings.workspace.automations.usage-overview-desc'
              ]?.() ?? 'Monthly workflow credit usage and execution breakdown.'}
            </div>
            <UsageAnalytics
              bare
              onlyTab="workflows"
              onPeriodLabelChange={setUsageOverviewPeriodTitle}
            />
          </SettingWrapper>
        ) : null}
        {isEditor ? <SettingsEditorScopeHint /> : null}
      </>
    </>
  );
};
